import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { currencyFormatter, formatDate, formatDateTime } from "../../Helpers/Utils";
import { DetailCard, Skeleton } from "../../Components/Needed";
import ErrorState from "../../Components/List/ErrorState";
import EmptyState from "../../Components/List/EmptyState";
import { FaCarSide } from "react-icons/fa";
import useListParams from "../../Helpers/useListParams";

const LIMIT = 8;

// Real rental history for this customer - reuses the existing booking ledger
// (POST /staff/booking/list/:pageNo), which already supports filtering by
// customerId, so nothing new had to be built on the backend for this tab.
const CustomerBookings = ({ customerId }) => {
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    // How many "Load more" pages are open lives in the query (?bkPage=3), so coming Back from a booking
    // shows the same rows instead of collapsing to the first few - see Helpers/useListParams.
    const { page, setPage } = useListParams({}, { query: true, prefix: "bk", zeroBased: true });
    const loadedRef = useRef(page);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // pages > 1 on the first load: fetch that many pages' worth in one call (same rows, same order).
    const load = useCallback(async (pageNo, append, pages = 1) => {
        setLoading(true);
        if (!append) setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/booking/list/${pageNo}`,
            params: { limit: LIMIT * pages },
            data: { customerId, sortBy: "createdAt", sort: true },
        });
        if (response?.success) {
            const data = response?.data?.data || {};
            setList((prev) => (append ? [...prev, ...(data.list || [])] : (data.list || [])));
            setHasNext(!!data.next);
            setError(null);
        } else if (!append) {
            setList([]);
            setError(response?.data?.message);
        }
        setLoading(false);
    }, [customerId]);

    useEffect(() => { load(0, false, loadedRef.current + 1); }, [load]);

    const loadMore = () => {
        const next = page + 1;
        load(next, true);
        setPage(next, { replace: true });
    };

    return (
        <DetailCard title="Recent Bookings" icon={<FaCarSide className="text-primary" size={15} />}>
            {loading && !list.length ? (
                <>
                    { [0, 1, 2].map((i) => <Skeleton key={i} height={22} className="mb-2" />) }
                </>
            ) : error && !list.length ? (
                <ErrorState message={error} onRetry={() => load(0, false)} />
            ) : !list.length ? (
                <EmptyState reason="empty" title="No bookings yet" description="This customer hasn't made a rental booking yet." />
            ) : (
                <>
                    <div className="mini-table-wrap">
                        <table className="mini-table">
                            <thead>
                                <tr>
                                    <th>Booking</th>
                                    <th>Vehicle</th>
                                    <th>Duration</th>
                                    <th>Stage</th>
                                    <th className="text-end">Amount</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                { list.map((item) => (
                                    <tr
                                        key={item.bookingId}
                                        className="mini-table-link-row"
                                        onClick={(event) => { if (!event.target.closest("a")) navigate(`/booking/details/${item.bookingId}`); }}
                                        title="Open booking"
                                    >
                                        <td className="fw-semibold"><Link to={`/booking/details/${item.bookingId}`}>{item.bookingId}</Link></td>
                                        <td>{item.carId || "-"}</td>
                                        <td>
                                            { item?.preBookDetails?.startDate
                                                ? `${formatDate(item.preBookDetails.startDate)} - ${formatDate(item.preBookDetails.endDate)}`
                                            : "-" }
                                        </td>
                                        <td><span className={`stage-pill stage-${item.bookingStage}`}>{item.bookingStage}</span></td>
                                        <td className="text-end">{currencyFormatter(item.totalAmount)}</td>
                                        <td className="text-muted">{formatDateTime(item.createdAt)}</td>
                                    </tr>
                                )) }
                            </tbody>
                        </table>
                    </div>
                    { hasNext ? (
                        <div className="text-center mt-3">
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading} onClick={loadMore}>
                                { loading ? "Loading..." : "Load more" }
                            </button>
                        </div>
                    ) : "" }
                </>
            )}
        </DetailCard>
    );
};

export default CustomerBookings;
